// src/pages/ProjetoSocial/ProjetoSocial.tsx
import * as React from "react";
import {
  Box,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  Link,
  Tooltip,
} from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import { useTranslation } from "react-i18next";
import { ICON_BASE_PATH, IMAGE_BASE_PATH } from "../../config/paths";
import { useNavigate } from "react-router-dom";
import { useSeo } from "../../components/seo/useSeo"; // ajuste o caminho se necessário
import { SITE_URL } from "../../config/site";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import Button from "@mui/material/Button";

const ProjetoSocial: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isXXLScreen = useMediaQuery(theme.breakpoints.up("xl"));
  const navigate = useNavigate();

  // =========================================================
  // SEO (title/description/canonical/og/twitter/locale)
  // =========================================================
  const rawLang = (i18n.resolvedLanguage || i18n.language || "pt").toLowerCase();
  const langPrefix = rawLang.split("-")[0]; // pt, es, en...
  const ogLocale =
    langPrefix === "pt" ? "pt_BR" : langPrefix === "en" ? "en_US" : "es_ES";

  const path = "/projetosocial";
  const canonical = `${SITE_URL}${path}`;

  const seoTitle = t("ProjetoSocial.seoTitle");
  const seoDescription = t("ProjetoSocial.seoDescription");

  useSeo({
    title: seoTitle,
    description: seoDescription,
    canonical,
    image: `${SITE_URL}${IMAGE_BASE_PATH}projetosocial/ProjetoSocialBanner.avif`,
    type: "website",
  });

  // garante locale correto (muitos parsers usam og:locale)
  React.useEffect(() => {
    const selector = `meta[property="og:locale"]`;
    let el = document.head.querySelector<HTMLMetaElement>(selector);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("property", "og:locale");
      document.head.appendChild(el);
    }
    el.setAttribute("content", ogLocale);
  }, [ogLocale]);

  // =========================================================
  // A11y helpers / IDs semânticos
  // =========================================================
  const titleId = "projeto-social-title";
  const descId = "projeto-social-desc";
  const instaLabel = t("ProjetoSocial.instaLabel");

  const manageFlagIcons = React.useCallback((pais: string) => {
    const common = { width: 36, height: 36 };
    switch (pais) {
      case "ALEMANHA":
        return (
          <img
            src={`${ICON_BASE_PATH}GermanIcon.png`}
            alt={t("Bandeiras.Alemanha")}
            style={common}
            loading="lazy"
            decoding="async"
          />
        );
      default:
        return (
          <img
            src={`${ICON_BASE_PATH}BrazilIcon.png`}
            alt={t("Bandeiras.Brasil")}
            style={common}
            loading="lazy"
            decoding="async"
          />
        );
    }
  }, [t]);

  const textoDescritivoStyle = React.useMemo(
    () => ({
      fontFamily: "Helvetica,Arial,Verdana,sans-serif",
      fontWeight: 400,
      lineHeight: 1.7,
      letterSpacing: "0.02em",
      fontSize: "1rem",
      color: "black",
      textAlign: isSmallScreen ? "center" : "left",
      mt: 3,
    }),
    [isSmallScreen]
  );

  const textoTituloStyle = React.useMemo(
    () => ({
      fontFamily: "Oswald, sans-serif",
      fontWeight: 700,
      fontSize: { xs: "2.3rem", sm: "2.3rem", md: "1.8rem", lg: "2.3rem", xl: "2.3rem" },
      letterSpacing: "-0.05em",
      textTransform: "uppercase",
      textAlign: isSmallScreen ? "center" : "left",
    }),
    [isSmallScreen]
  );

  const textoSubTituloStyle = React.useMemo(
    () => ({
      fontFamily: "Oswald, sans-serif",
      fontWeight: 600,
      fontSize: { xs: "1.6rem", sm: "1.6rem", md: "1.3rem", lg: "1.6rem", xl: "1.6rem" },
      letterSpacing: "-0.03em",
      textTransform: "uppercase",
      textAlign: isSmallScreen ? "center" : "left",
    }),
    [isSmallScreen]
  );

  // Conteúdo do texto (SEM mudar ordem)
  const textoBoxInstitutoSer = React.useMemo(() => {
    return (
      <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
        <Box
          sx={{
            color: "#fff",
            textTransform: "none",
            maxWidth: isSmallScreen ? "100%" : "680px",
            ml: "auto",
            textAlign: isSmallScreen ? "center" : "right",
            justifyContent: "center",
          }}
        >
          {/* H1 único na página */}
          <Typography
            id={titleId}
            component="h1"
            sx={textoTituloStyle}
            color="black"
          >
            {t("ProjetoSocial.institutoSerTitulo")}
          </Typography>

          {/* Subtítulo como H2 */}
          <Typography component="h2" sx={textoSubTituloStyle} color="black">
            {t("ProjetoSocial.institutoSerSubtitulo")}
          </Typography>

          {/* Descrição principal ligada ao aria-describedby */}
          <Typography id={descId} component="p" sx={textoDescritivoStyle} color="black">
            {t("ProjetoSocial.institutoSerDescricao")}
          </Typography>

          <Typography component="p" sx={textoDescritivoStyle} color="black">
            {t("ProjetoSocial.institutoSerDescricao2")}
          </Typography>
        </Box>
      </Grid>
    );
  }, [t, isSmallScreen, textoDescritivoStyle, textoSubTituloStyle, textoTituloStyle, titleId, descId]);

  const handleGoInstituto = React.useCallback(() => {
    navigate("/institutoser");
  }, [navigate]);

  return (
    <Box component="main" aria-labelledby={titleId} aria-describedby={descId}>
      <Grid container spacing={4} justifyContent="center" sx={{ backgroundColor: "white" }}>
        {/* HERO */}
        <Grid size={12} sx={{ height: "80vh" }}>
          <Box
            sx={{
              position: "relative",
              height: "100%",
              width: "100%",
              overflow: "hidden",
            }}
          >
            {/* IMPORTANTE SEO: banner é conteúdo relevante => alt descritivo */}
            <img
              src={`${IMAGE_BASE_PATH}projetosocial/ProjetoSocialBanner.avif`}
              alt={t("ProjetoSocial.bannerAlt")}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </Box>
        </Grid>

        {/* CONTEÚDO */}
        <Grid
          container
          spacing={4}
          justifyContent="center"
          sx={{ display: "flex", alignItems: "flex-start", height: "100%", p: 2 }}
        >
          {/* mantém o layout original: texto antes no desktop, depois no mobile */}
          {!isSmallScreen && textoBoxInstitutoSer}

          <Grid
            size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
            display="flex"
            justifyContent={isXXLScreen ? "start" : "center"}
          >
            <Box
              sx={{
                ml: isXXLScreen ? 20 : undefined,
                position: "relative",
                height: "100%",
                width: { xs: "100%", sm: "100%", md: "100%", lg: "100%", xl: "100%" },
                maxWidth: "400px",
                overflow: "hidden",
              }}
            >
              <Tooltip title={t("ProjetoSocial.saibaMais")}>
                {/* A11y + SEO: imagem clicável -> role/link + aria-label */}
                <Box
                  component="a"
                  href={`${path === "/projetosocial" ? "" : ""}`} // sem navegação externa aqui
                  onClick={(e) => {
                    e.preventDefault();
                    handleGoInstituto();
                  }}
                  aria-label={t("ProjetoSocial.institutoSerOpenPage")}
                  style={{ display: "block" }}
                >
                  <Box
                    component="img"
                    src={`${IMAGE_BASE_PATH}projetosocial/InstitutoSerBrasil.avif`}
                    alt={t("ProjetoSocial.institutoSerImgAlt")}
                    sx={{
                      transition: "transform 0.3s ease, filter 0.3s ease",
                      cursor: "pointer",
                      "&:hover": {
                        transform: "scale(1.05)",
                        opacity: 0.7,
                      },
                      objectFit: "cover",
                      width: "100%",
                      height: "100%",
                      display: "block",
                    }}
                    loading="lazy"
                    decoding="async"
                  />
                </Box>
              </Tooltip>

              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                sx={{
                  width: "100%",
                  maxWidth: "700px",
                  mt: "8px",
                }}
              >
                {/* Nome e país */}
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="center"
                  sx={{ mb: "4px" }}
                >
                  {manageFlagIcons("BRASIL")}
                  <Typography
                    variant="h6"
                    component="h3"
                    gutterBottom
                    sx={{
                      fontFamily: "Lato, sans-serif",
                      fontWeight: 400,
                      color: "black",
                      ml: 1,
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      mb: 0,
                    }}
                  >
                    INSTITUTO SER
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  component="p"
                  sx={{
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 500,
                    color: "grey",
                    mt: -1,
                    mb: 3,
                  }}
                >
                  PRÊA - CE
                </Typography>

                {/* Barra do Instagram */}
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="center"
                  sx={{
                    backgroundColor: "#FFDE00",
                    py: 0.5,
                    width: "100%",
                    borderRadius: 1,
                    maxWidth: "400px",
                  }}
                >
                  <InstagramIcon sx={{ color: "black", mt: 0.3 }} aria-hidden="true" />
                  <Link
                    href="https://www.instagram.com/projetovivokitesurf/"
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                    aria-label={instaLabel}
                    sx={{ textDecoration: "none" }}
                  >
                    <Typography
                      variant="body1"
                      component="span"
                      sx={{
                        fontWeight: "bold",
                        color: "black",
                        ml: "8px",
                      }}
                    >
                      @INSTITUTOSER.BRASIL
                    </Typography>
                  </Link>
                </Box>

                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    handleGoInstituto();
                  }}
                  variant="contained"
                  disableElevation
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{
                    mt: 1.5,

                    width: "100%",
                    maxWidth: "400px",     // ✅ igual ao Instagram
                    borderRadius: 1,       // ✅ igual

                    typography: "body1",
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",

                    backgroundColor: "#FFDE00",
                    color: "black",
                    minHeight: 36,        
                    py: 0.5,               
                    px: 1.5,

                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#111",
                      color: "white",
                      boxShadow: "none",
                    },
                  }}
                  aria-label={t("ProjetoSocial.institutoSerOpenPage")}
                >
                  {t("ProjetoSocial.saibaMais")}
                </Button>
              </Box>
            </Box>
          </Grid>

          {isSmallScreen && textoBoxInstitutoSer}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProjetoSocial;
